/* Toast CSS Module */

/* ============================================
   Toast Actions Container
   ============================================ */
.toastActions {
  align-items: center;
  align-self: stretch;
  display: flex;
  flex-shrink: 0;
}

/* ============================================
   Toast Close Button Container
   CSS variables are declared in ToastAnimationDisplay
   ============================================ */
.toastButtonCloseContainer {
  align-items: center;
  align-self: stretch;
  display: flex;
  flex-shrink: 0;
  margin-bottom: calc(var(--vapor-toast-vertical-padding) * -1);
  margin-left: var(--spaces-8);
  margin-top: calc(var(--vapor-toast-vertical-padding) * -1);
}

/* ============================================
   Toast Adornment Before Container (icons)
   ============================================ */
.toastAdornmentBeforeContainer {
  align-items: center;
  align-self: stretch;
  display: flex;
}

/* ============================================
   Toast Display Messages
   ============================================ */
.toastDisplayMessages {
  align-items: center;
  align-self: stretch;
  column-gap: var(--spaces-4);
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  margin-inline-end: var(--spaces-12);
  padding-inline-start: var(--spaces-8);
  row-gap: var(--spaces-2);
  white-space: pre-wrap;
}

/* ============================================
   Toast Motion Box - main toast content wrapper
   ============================================ */
.toastMotionBox {
  border-radius: var(--vapor-toast-radius);
  box-sizing: border-box;

  display: flex;
  min-height: var(--vapor-toast-min-height);
  overflow: hidden;
  padding-block: var(--vapor-toast-vertical-padding);
  padding-inline: var(--vapor-toast-horizontal-padding);
  width: auto;

  &[data-loading] {
    left: 0;
    min-height: var(--vapor-toast-min-height);
    position: absolute;
    top: 0;
  }

  @media screen and (min-width: 768px) {
    width: auto;

    &[data-loading] {
      min-width: 260px;
      width: 260px;
    }
  }

  /* Conditionally add spacing when icons are present */
  &:has(.toastAdornmentBeforeContainer) .toastDisplayMessages {
    padding-inline-start: var(--spaces-8);
  }

  /* Conditionally adjust margin when actions are present */
  &:has(.toastActions) .toastDisplayMessages {
    margin-inline-end: var(--spaces-16);
  }
}

/* Toast Motion Box with horizontal boundaries (for alerts) */
.toastMotionBox[data-has-horizontal-boundaries] {
  @media screen and (min-width: 768px) {
    max-width: 448px;
    min-width: 360px;

    /* Loading state needs higher specificity to override min-width */
    &[data-loading] {
      min-width: 260px;
      width: 260px;
    }
  }
}

/* ============================================
   Toast Loading Indicator
   ============================================ */
@keyframes toastBounce {
  0% {
    transform: translateX(-100%);
  }

  100% {
    transform: translateX(100%);
  }
}

.toastLoadingIndicator {
  animation-duration: 1000ms;
  animation-iteration-count: infinite;
  animation-name: toastBounce;
  animation-timing-function: ease-in-out;
  background: linear-gradient(
    to right,
    transparent,
    var(--colors-brand-primary)
  );
  height: var(--spaces-2);
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  width: 100%;
}

/* ============================================
   Toast Shortcuts Container
   ============================================ */
.toastShortcuts {
  column-gap: var(--spaces-2);
  display: inline-flex;
  padding-inline: var(--spaces-4);
}

/* ============================================
   Toast Button Group
   ============================================ */
.toastButtonGroup {
  display: flex;
  gap: var(--spaces-8);
}

/* ============================================
   Toast Container (fixed positioning)
   ============================================ */
.toastContainer {
  align-items: center;
  contain: layout;
  display: flex;
  left: var(--vapor-toast-container-margin);
  pointer-events: none;
  position: fixed;
  right: var(--vapor-toast-container-margin);
  z-index: var(--z-indices-toast);

  &:where([data-placement^='bottom']) {
    bottom: var(--vapor-toast-container-margin);
    flex-direction: column-reverse;
  }

  &:where([data-placement^='top']) {
    flex-direction: column;
    top: var(--vapor-toast-container-margin);
  }

  &:where([data-placement*='left']) {
    align-items: flex-start;
  }

  &:where([data-placement*='right']) {
    align-items: flex-end;
  }
}

/* ============================================
   Toast Item Wrapper
   ============================================ */
.toastItemWrapper {
  box-sizing: border-box;
  pointer-events: none;
  position: absolute;
  transition: transform 0.3s ease;
  user-select: none;
  will-change: transform;
  z-index: 1;

  @media (prefers-reduced-motion) {
    transition: none;
  }

  &:where([data-exiting]) {
    z-index: 0;
  }
}

/* ============================================
   Toast Item Animation Display
   Keyframes and animations for toast entry/exit
   ============================================ */
@keyframes enterTop {
  from {
    opacity: 0;
    transform: translateY(-24px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes exitTop {
  from {
    opacity: 1;
    transform: translateY(0);
  }

  to {
    opacity: 0;
    transform: translateY(-24px);
  }
}

@keyframes enterBottom {
  from {
    opacity: 0;
    transform: translateY(24px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes exitBottom {
  from {
    opacity: 1;
    transform: translateY(0);
  }

  to {
    opacity: 0;
    transform: translateY(24px);
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.animatedToastItem {
  --vapor-toast-item-padding: var(--spaces-8);

  animation-duration: 200ms;
  animation-name: enterBottom;
  animation-timing-function: var(--transitions-eases-primary);
  padding: 0 var(--vapor-toast-item-padding) var(--vapor-toast-item-padding)
    var(--vapor-toast-item-padding);
  pointer-events: auto;
  will-change: transform, opacity;

  @media (prefers-reduced-motion) {
    animation-name: fadeIn;
  }

  &:where([data-exiting]) {
    animation-duration: 100ms;
    animation-fill-mode: forwards;
    animation-name: exitBottom;
    animation-timing-function: ease-in;

    @media (prefers-reduced-motion) {
      animation-name: fadeOut;
    }
  }

  &:where([data-top]) {
    animation-name: enterTop;
    padding: var(--vapor-toast-item-padding) var(--vapor-toast-item-padding) 0
      var(--vapor-toast-item-padding);

    @media (prefers-reduced-motion) {
      animation-name: fadeIn;
    }

    &:where([data-exiting]) {
      animation-name: exitTop;

      @media (prefers-reduced-motion) {
        animation-name: fadeOut;
      }
    }
  }

  /* When the user swipes to dismiss the toast */
  &:where([data-dismissed-via-drag][data-exiting]) {
    animation-name: fadeOut;
  }

  .toastMotionBox {
    /* Only apply the full-bleed mobile width inside the animated item wrapper */
    width: calc(100dvw - var(--vapor-toast-item-padding, 0px));

    @media screen and (min-width: 768px) {
      width: auto;
    }
  }
}

/* ============================================
   Toast Animation Display (outer wrapper)
   ============================================ */
.styledToast {
  background: var(--colors-components-toast-background);
  border-radius: var(--vapor-toast-radius);
  box-shadow: 0 0 0 1px var(--colors-border-primary) inset, var(--shadows-12dp);
  overflow: hidden;
  position: relative;
  user-select: none;
  will-change: height, width;
}
